import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LessonOptions from '../utils/LessonOptions';
import { expTier, statusesOf, lessonStatus, trimNum, lessonDateParts, lessonTimeText } from '../utils/MyCardView';
// v0.19.18 — 分区骨架与「新建客户跟进」**共用一个入口** (本仓铁律「一个逻辑一个入口」)。
//   卡片详情要跟新建跟进用同一套分区排版, 骨架原本是 SalesFollowupCreateModal 的类方法
//   ⇒ 两边各抄一份迟早分叉 ⇒ 收进 utils/FollowupForm.js, 两个弹层都 import。
import { renderSection } from '../utils/FollowupForm';
// v0.19.20 (批 41 · 段②) — 「字段图标」改用**共用入口**
//   (本仓铁律「一个逻辑一个入口, 不许抄两遍」; 改前这个原语在本文件里抄了一份)。
//   ⚠️ 上一行对 `utils/FollowupForm` 的 import **一个字都没改** (批 39 已上线的产物, 零扰动)。
import { fieldIcon } from '../utils/FormPrimitives';

/**
 * v0.19.18 — 「我的卡片」**卡片详情弹层** 改版: 排版对齐「新建客户跟进」
 *   (辉哥 10/27 拍板「我的卡片tab中，卡片详情modal要按照"新建客户跟进"modal那样的样式和排版来做」)
 *
 * ## 改版后的形态 (设计稿: design-v01916-卡片详情改版.html 屏 1 / 屏 4)
 *   · **Flarum 默认标题栏** (图标 + 卡名) —— ⛔ 删掉了旧的 `inner()` 自定义标题栏
 *     (旧的把「卡名 + 状态签 + 校区名」拼成两行; 手机端 core 标题控件只有 200px 宽,
 *      塞不下卡名 + 状态签 ⇒ 状态签挪到 ② 区「当前状态」字段)。
 *   · **三段分区** (`.ZS-section` 体系, 与「新建客户跟进」同一套):
 *       ① 课时概览 (剩余课时 / 总课时 + 使用进度条)
 *       ② 卡片信息 (8 个字段, 两列)
 *       ③ 上课记录 (该卡名下的课次)
 *   · 字段全部用**只读输入框** (`.ZS-form-input` + `readonly`, 文字仍可选中)。
 *   · 底部**只有**一个 `.Button--primary`「知道了」 (⛔ 不加「去约课」之类行动入口)。
 *
 * ## ⛔ 纯展示: **不额外发任何请求**
 *   数据全部来自 `this.attrs.card` (列表接口 `GET /my-cards` 已经把它带回来了):
 *   卡详情字段在卡对象上, 课次在 `card.lessons[]` 里。
 *   ⛔ **不新开详情端点** (员工端 `CardDetailModal.js` 就是这个套路: 数据父级传入)。
 *   ⇒ 弹层打开**零网络往返** —— 点开即见, 不会白屏一下。
 *
 * ## ⛔ 不动的部分 (逐字保留)
 *   · `primaryStatus()` / `categoriesText()` / `typeText()` / `minutesText()` /
 *     `expiresText()` / `schoolName()` / `usedCount()` / `usedPercent()`
 *   · 口径全部走 `utils/MyCardView.js` (状态签 / 有效期 / 课次状态词 / 日期时段)
 *     —— 批 35 已把 `lessonDateParts()` / `lessonTimeText()` 搬进那里, ⛔ 不要搬回来。
 *
 * ## 样式作用域锁
 *   `ZS-MyCardDetailModal` 是**样式作用域锁** (less/forum.less 里所有覆盖规则挂在它下面)
 *   ⇒ className 里**不能删**。
 */
export default class MyCardDetailModal extends Modal {
  className() {
    // v0.19.18 — 对齐「新建客户跟进」: Modal--medium (深色 body 配色同款) + 移动端满宽
    //   ⚠️ `ZS-MyCardDetailModal` 仍是**样式作用域锁** (less 里所有覆盖规则挂在它下面) ⇒ 不能删
    return 'Modal--medium Modal--fullscreen-padded ZS-MyCardDetailModal';
  }

  /**
   * Flarum 默认标题栏 (`<h3 class="App-titleControl App-titleControl--text">`) 的内容。
   *
   * ⚠️ **不要**在标题里放状态签: 手机端 core 的 `.App-titleControl` 只有 200px 宽,
   *   卡名 + 状态签会被截掉一半 ⇒ 状态签在 ② 区「当前状态」字段里。
   */
  title() {
    const c = this.attrs.card || {};
    return m("span", {
      className: "ZS-mcd-titlebar"
    }, m("i", {
      className: "fas fa-id-card",
      style: {
        color: 'var(--zs-cyan-06b6d4, #06b6d4)',
        marginRight: '8px'
      }
    }), c.name || '学员卡 #' + c.id);
  }

  /** 这张卡的主状态签 (优先级第一个: 未激活 > 已过期 > 课时用完 > 生效中)。 */
  primaryStatus() {
    const sts = statusesOf(this.attrs.card || {});
    return sts.length ? sts[0] : {
      k: 'st-active',
      label: '生效中',
      icon: 'fa-circle-check'
    };
  }

  /** 课程分类 id 列表 → 名字串 (「双板、单板」)。 */
  categoriesText() {
    const c = this.attrs.card || {};
    const ids = Array.isArray(c.lesson_category_ids) ? c.lesson_category_ids : [];
    if (!ids.length) return '未分类';
    return ids.map(cid => LessonOptions.labelFor('category', cid)).join('、');
  }

  /** 课程类型 → 名字 (没有就「未设置」)。 */
  typeText() {
    const c = this.attrs.card || {};
    if (!c.lesson_type_id) return '未设置';
    return LessonOptions.labelFor('type', c.lesson_type_id);
  }

  /** 单次时长。 */
  minutesText() {
    const c = this.attrs.card || {};
    return c.minutes ? c.minutes + ' 分钟' : '未设置';
  }

  /** 有效期 (走 expTier, 与卡面**同一套**口径)。 */
  expiresText() {
    const c = this.attrs.card || {};
    return expTier(c).text;
  }

  /** 校区名 (父级传入; 没有就「未设置校区」)。 */
  schoolName() {
    const c = this.attrs.card || {};
    return this.attrs.schoolName || c.school_name || '未设置校区';
  }

  /** 已用次数 = 总课时 - 剩余课时 (无限次卡没有这个概念 ⇒ null)。 */
  usedCount() {
    const c = this.attrs.card || {};
    if (c.is_unlimited) return null;
    const total = Number(c.total_lessons) || 0;
    const remain = Number(c.remaining_lessons) || 0;
    const used = total - remain;
    return used > 0 ? used : 0;
  }

  /** 进度条百分比 (无限次卡 ⇒ null = 不渲染进度条)。 */
  usedPercent() {
    const c = this.attrs.card || {};
    if (c.is_unlimited) return null;
    const total = Number(c.total_lessons) || 0;
    if (total <= 0) return 0;
    const used = this.usedCount();
    const pct = Math.round(used / total * 100);
    return Math.max(0, Math.min(100, pct));
  }

  /**
   * v0.19.15 — 上课日期与时段的**显示口径已搬进 `utils/MyCardView.js`**
   *   (`lessonDateParts` / `lessonTimeText`) —— 因为「学员自主约课」弹窗也要显示同一份,
   *   两处各抄一遍就是「同一个逻辑两个入口」, 迟早分叉 (本仓铁律)。
   *   ⇒ 本文件直接用共享那两个, 逐字同款, 行为零变化。
   */

  // ── 小组件 ──────────────────────────────────────────────────────

  /** 字段前的图标 (写法与「新建客户跟进」的 fieldIcon 同款)。
   *  ⚠️ v0.19.20 (批 41 · 段②) — 实现**搬**进共用入口 `utils/FormPrimitives.js`
   *    (改前 `fieldIcon` 在本文件里抄了一份 ⇒ 本仓铁律「一个逻辑一个入口」)
   *    ⇒ 本文件的 DOM 结构与样式**一字不动**。 */

  /**
   * 一个**只读字段** (label + 只读输入框)。
   *
   * ⛔ 不用 `disabled` (会变灰且文字不可选) · ⛔ 用 `value` 不用 `defaultValue` (Mithril 受控)。
   */
  roGroup(cls, icon, label, value) {
    return m("div", {
      className: 'ZS-form-group ' + cls
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon(icon), " ", label), m("input", {
      className: "ZS-form-input ZS-mcd-ro",
      value: value,
      readOnly: true
    }));
  }

  // ── 三段分区 ────────────────────────────────────────────────────

  /** 分区① 课时概览 (剩余课时 / 总课时 + 使用进度条)。 */
  renderSectionLessons() {
    const c = this.attrs.card || {};
    const unlimited = !!c.is_unlimited;
    const pct = this.usedPercent();
    const used = this.usedCount();
    const remainText = unlimited ? '不限次' : trimNum(c.remaining_lessons) + ' 次';
    const totalText = unlimited ? '不限次' : trimNum(c.total_lessons) + ' 次';
    const caption = unlimited ? '不限次' : '已用 ' + used + ' 次 · 共 ' + trimNum(c.total_lessons) + ' 次';
    const children = [m("div", {
      className: "ZS-form-row",
      key: "row1"
    }, this.roGroup('ZS-fg-remain', 'fa-hourglass-half', '剩余课时', remainText), this.roGroup('ZS-fg-total', 'fa-list-check', '总课时', totalText))];

    // 无限次卡没有「已用 N 次」这个概念 ⇒ 整行不渲染 (⛔ 不编一个假数字)
    if (pct !== null) {
      children.push(m("div", {
        className: "ZS-form-row full",
        key: "row2"
      }, m("div", {
        className: "ZS-form-group ZS-fg-bar ZS-fg-full"
      }, m("div", {
        className: "ZS-form-label"
      }, fieldIcon('fa-chart-simple'), " \u4F7F\u7528\u8FDB\u5EA6"), m("div", {
        className: "ZS-mcd-bar-row",
        style: "margin-top:0"
      }, m("div", {
        className: 'ZS-mcd-bar' + (pct > 0 ? ' is-used' : '')
      }, m("div", {
        className: "ZS-mcd-bar-fill",
        style: {
          width: pct + '%'
        }
      })), m("div", {
        className: "ZS-mcd-bar-note"
      }, "\u5DF2\u7528 ", used, " \u6B21")))));
    }
    return renderSection(1, '课时概览', caption, children);
  }

  /** 分区② 卡片信息 (8 个字段, 两列)。 */
  renderSectionCardInfo() {
    const c = this.attrs.card || {};
    const st = this.primaryStatus();
    return renderSection(2, '卡片信息', '8 个字段', [m("div", {
      className: "ZS-form-row",
      key: "row1"
    }, this.roGroup('ZS-fg-cardname', 'fa-id-badge', '卡种名称', c.name || '学员卡 #' + c.id), this.roGroup('ZS-fg-school', 'fa-location-dot', '校区', this.schoolName())), m("div", {
      className: "ZS-form-row",
      key: "row2"
    }, this.roGroup('ZS-fg-minutes', 'fa-clock', '单次时长', this.minutesText()), this.roGroup('ZS-fg-expire', 'fa-calendar-days', '有效期', this.expiresText())), m("div", {
      className: "ZS-form-row",
      key: "row3"
    }, this.roGroup('ZS-fg-type', 'fa-tag', '课程类型', this.typeText()), this.roGroup('ZS-fg-cate', 'fa-tags', '课程分类', this.categoriesText())), m("div", {
      className: "ZS-form-row",
      key: "row4"
    }, m("div", {
      className: "ZS-form-group ZS-fg-status"
    }, m("div", {
      className: "ZS-form-label"
    }, fieldIcon('fa-circle-info'), " \u5F53\u524D\u72B6\u6001"), m("div", {
      className: "ZS-mcd-state-box"
    }, m("span", {
      className: 'ZS-mc-chip ' + st.k
    }, m("i", {
      className: 'fas ' + st.icon
    }), st.label))), this.roGroup('ZS-fg-active', 'fa-toggle-on', '激活状态', c.is_activated ? '已激活' : '未激活'))]);
  }

  /** 分区③ 上课记录 (该卡名下的课次; 按 start_at 升序, 后端已排好)。 */
  renderSectionRecords() {
    const c = this.attrs.card || {};
    const lessons = Array.isArray(c.lessons) ? c.lessons : [];
    const body = lessons.length === 0 ? /* 空态: 面向学员的一句话 (⛔ 不许「暂无」这种干巴巴的词) */
    m("div", {
      className: "ZS-mcd-empty",
      key: "empty"
    }, m("i", {
      className: "fas fa-calendar-xmark"
    }), m("p", null, "\u8FD9\u5F20\u5361\u8FD8\u6CA1\u6709\u4E0A\u8BFE\u8BB0\u5F55")) : m("div", {
      className: "ZS-mcd-lessons",
      key: "lessons"
    }, lessons.map(l => {
      const dp = lessonDateParts(l.start_at);
      const st = lessonStatus(l.status);
      return m("div", {
        className: "ZS-mcd-lesson",
        key: l.id
      }, m("div", {
        className: "ZS-mcd-ls-date"
      }, m("div", {
        className: "ZS-mcd-ls-d1"
      }, dp.d1), m("div", {
        className: "ZS-mcd-ls-d2"
      }, dp.d2)), m("div", {
        className: "ZS-mcd-ls-time"
      }, lessonTimeText(l)), m("div", {
        className: "ZS-mcd-ls-coach"
      }, m("i", {
        className: "fas fa-user"
      }), l.coach_name || '未指定教练'), m("div", {
        className: "ZS-mcd-ls-st"
      }, m("span", {
        className: 'ZS-status-tag ' + st.class
      }, st.label)));
    }));
    return renderSection(3, '上课记录', '共 ' + lessons.length + ' 条', [body]);
  }

  /**
   * 弹层内容。
   *
   * ⚠️ **必须返回数组**: Flarum 的默认 `inner()` 返回一个 fragment, `content()` 的返回值
   *   落在 `.Modal-content` 里; 而 `.Modal-content` 是 `display:flex; flex-direction:column`
   *   (旧段的骨架, 正好就是我们要的「内容区滚 + 头尾常驻」)
   *   ⇒ 返回数组才能让 `.Modal-body` / `.Modal-footer` 成为它的**直接子节点**,
   *     否则会被包在一个 `<div>` 里, flex 三段布局落不到 body 上。
   */
  content() {
    return [m("div", {
      className: "Modal-body",
      key: "body"
    }, m("div", {
      className: "ZS-modal-form ZS-followup-form"
    }, this.renderSectionLessons(), this.renderSectionCardInfo(), this.renderSectionRecords())), m("div", {
      className: "Modal-footer",
      key: "footer"
    }, m(Button, {
      className: "Button Button--primary",
      onclick: () => this.hide()
    }, "\u77E5\u9053\u4E86"))];
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/MyCardDetailModal', MyCardDetailModal);